import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import ReactECharts from 'echarts-for-react'

import { useApi } from '@/hooks'
import type { ApiService } from '@/types/dashboard-types'

import '../historyGraph/echarts-setup'

import { buildTimelineSections } from './buildTimelineSections'
import { useTimelineChartOptions } from './useTimelineChartOptions'

import type { AssetHistory, AssetHistoryChartProps, ZoomRange } from '../historyGraph/types'

export default function AssetTimelineChart({ data, reload, from, periodHours }: AssetHistoryChartProps) {
  const chartRef = useRef<ReactECharts>(null)
  const api: ApiService = useApi().api
  const [currentData, setCurrentData] = useState<AssetHistory | undefined>(data)
  const [periodOffset, setPeriodOffset] = useState(0)
  const [hasNavigated, setHasNavigated] = useState(false)
  const [hiddenRows, setHiddenRows] = useState<Set<string>>(new Set())

  const periodWindow: ZoomRange = useMemo(() => {
    const start = from.plus({ hours: periodOffset * periodHours })
    const end = start.plus({ hours: periodHours })
    return [start.toMillis(), end.toMillis()]
  }, [from, periodOffset, periodHours])

  const sections = useMemo(
    () => (currentData ? buildTimelineSections(currentData, periodWindow) : []),
    [currentData, periodWindow]
  )

  const { option, height } = useTimelineChartOptions(sections, periodWindow, hiddenRows)

  const navigate = useCallback((direction: -1 | 1) => {
    setPeriodOffset((prev) => prev + direction)
    setHasNavigated(true)
  }, [])

  useEffect(() => {
    if (!api || !hasNavigated) return
    const fn = async () => {
      const start = from.plus({ hours: periodOffset * periodHours })
      const newData = await reload(api, { from: start, to: start.plus({ hours: periodHours }) })
      setCurrentData(newData)
    }
    fn()
  }, [api, periodOffset, reload, hasNavigated])

  // Click handler for y-axis label × dismiss
  const handleChartClick = useCallback((params: { componentType?: string; targetType?: string; value?: string }) => {
    if (params.componentType === 'yAxis' && params.targetType === 'axisLabel' && params.value) {
      // params.value is the unique row key used as category axis value
      setHiddenRows((prev) => {
        const next = new Set(prev)
        next.add(params.value!)
        return next
      })
    }
  }, [])

  const handleReset = useCallback(() => {
    setHiddenRows(new Set())
  }, [])

  if (!sections.length) {
    return <div className="text-sm text-gray-500 p-4">No timeline data available</div>
  }

  return (
    <div>
      {hiddenRows.size > 0 && (
        <div className="flex justify-end px-2 pt-1">
          <button
            onClick={handleReset}
            className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
          >
            Reset ({hiddenRows.size} hidden)
          </button>
        </div>
      )}

      <ReactECharts
        ref={chartRef}
        option={option}
        notMerge={true}
        lazyUpdate={true}
        style={{ height, width: '100%' }}
        onEvents={{ click: handleChartClick }}
      />

      {/* Navigation buttons */}
      <div className="flex items-center justify-center gap-4 py-1">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
        >
          &larr; {periodHours}hr
        </button>
        <button
          onClick={() => navigate(1)}
          className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
        >
          {periodHours}hr &rarr;
        </button>
      </div>
    </div>
  )
}
